import React, { useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight, ArrowDownRight, Plus, Target, XCircle, Hourglass, Lightning, Crosshair,
} from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import ModeToggle from "@/components/ModeToggle";
import { ModeContext } from "@/contexts/ModeContext";
import { fetchTimeline } from "@/lib/api";
import { fmtRelative, fmtPrice } from "@/lib/api";
import { AUREUS } from "@/constants/testIds";

const EVENT_META = {
  CREATED:    { Icon: Plus,        color: "text-zinc-400", chip: "chip-neutral", label: "Forming" },
  ENTERED:    { Icon: Crosshair,   color: "text-gold",     chip: "chip-gold",    label: "Entered" },
  TP1_HIT:    { Icon: Target,      color: "text-bull",     chip: "chip-bull",    label: "TP1" },
  TP2_HIT:    { Icon: Target,      color: "text-bull",     chip: "chip-bull",    label: "TP2" },
  SL_HIT:     { Icon: XCircle,     color: "text-bear",     chip: "chip-bear",    label: "SL" },
  EXPIRED:    { Icon: Hourglass,   color: "text-zinc-500", chip: "chip-neutral", label: "Expired" },
  INVALIDATED:{ Icon: XCircle,     color: "text-zinc-500", chip: "chip-neutral", label: "Invalidated" },
};

const FILTERS = [
  { id: "all",   label: "All"   },
  { id: "snipe", label: "Trade Zone" },
  { id: "scalp", label: "Short Zone" },
];

function EventRow({ ev }) {
  const meta = EVENT_META[ev.event_type] || EVENT_META.CREATED;
  const ModeIcon = ev.mode === "scalp" ? Lightning : Crosshair;
  const BiasIcon = ev.event_type === "SL_HIT" || ev.event_type === "EXPIRED" ? ArrowDownRight : ArrowUpRight;
  return (
    <div
      data-testid={AUREUS.timelineEvent(ev.id)}
      className="flex items-center gap-4 px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition"
    >
      <div className={`w-9 h-9 flex items-center justify-center border border-white/10 ${meta.color}`}>
        <meta.Icon size={16} weight="regular" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="heading text-sm font-semibold">{ev.symbol}</span>
          <span className={`chip ${meta.chip}`}>{meta.label}</span>
          <span className="chip chip-neutral">
            <ModeIcon size={10} weight="bold" /> {ev.mode}
          </span>
          {ev.price != null && (
            <span className="mono text-xs text-zinc-500">@ {fmtPrice(ev.price)}</span>
          )}
        </div>
        <div className="text-sm text-zinc-300 mt-1 truncate">{ev.message}</div>
      </div>
      <div className="mono text-xs text-zinc-500 whitespace-nowrap">{fmtRelative(ev.created_at)}</div>
      <BiasIcon size={14} className={`${meta.color} hidden sm:block`} />
    </div>
  );
}

export default function TimelinePage() {
  const [filter, setFilter] = useState("all");
  const { mode: globalMode } = useContext(ModeContext);

  const params = useMemo(() => {
    if (filter === "all") return { limit: 80 };
    return { limit: 80, mode: filter };
  }, [filter]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["timeline", params],
    queryFn: () => fetchTimeline(params),
    refetchInterval: 4000,
  });

  return (
    <AppShell>
      <div className="p-6 md:p-10" data-testid={AUREUS.timelinePage}>
        <header className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="overline mb-3">Lifecycle Stream</div>
            <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter">
              Every setup. <span className="text-gold-grad">Every step.</span>
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl text-sm">
              Real-time stream of setup creation, entry, target hits and stop-outs across all
              six instruments. Active trading mode: <span className="text-white">{globalMode}</span>.
            </p>
          </div>
          <ModeToggle />
        </header>

        <div data-testid={AUREUS.timelineFilters} className="flex items-center gap-2 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                filter === f.id
                  ? "border-gold/60 bg-gold/10 text-gold"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading && <div className="surface h-[500px] animate-pulse" />}
        {error && <div className="surface p-6 text-bear">Failed to load timeline: {String(error.message || error)}</div>}

        {data?.events && (
          <div className="surface">
            {data.events.length === 0 ? (
              <div className="p-10 text-center text-zinc-500 text-sm">No events yet — setups should crystallize within ~30s.</div>
            ) : (
              data.events.map((ev) => <EventRow key={ev.id} ev={ev} />)
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
